import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Layers, RefreshCw, AlertTriangle, Trash2, History, Undo2, ArrowDownLeft, ArrowUpRight, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => (s || '').slice(0, 10);

function DuplicateSummary({ counts, onRefresh, refreshing }) {
    const total = (counts?.companies || 0) + (counts?.invoices || 0) + (counts?.transactions || 0);
    return (
        <div className={`rounded-2xl border-2 p-5 ${total === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="acc-dup-summary">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    {total === 0
                        ? <CheckCircle2 size={28} className="text-emerald-600" />
                        : <AlertTriangle size={28} className="text-amber-600" />}
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Duplicate Genel Bakis</p>
                        <p className={`text-2xl font-bold ${total === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {total === 0 ? 'Hic duplicate yok' : `${total} duplicate kayit`}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <MiniCount label="Firma" value={counts?.companies || 0} accent="purple" />
                    <MiniCount label="Fatura grubu" value={counts?.invoices || 0} accent="blue" />
                    <MiniCount label="Transfer grubu" value={counts?.transactions || 0} accent="red" />
                    <MiniCount label="Birlesmis" value={counts?.history || 0} accent="emerald" />
                </div>
                <button onClick={onRefresh} disabled={refreshing}
                    className="h-9 px-3 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] text-xs font-semibold rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="acc-dup-summary-refresh">
                    {refreshing ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Yenile
                </button>
            </div>
        </div>
    );
}

function MiniCount({ label, value, accent }) {
    const cls = {
        purple: 'bg-purple-100 text-purple-800 border-purple-200',
        blue: 'bg-blue-100 text-blue-800 border-blue-200',
        red: 'bg-red-100 text-red-800 border-red-200',
        emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    }[accent] || 'bg-gray-100 text-gray-800 border-gray-200';
    return (
        <div className={`rounded-xl border px-3 py-2 ${cls}`}>
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-70">{label}</p>
            <p className="text-lg font-bold">{value}</p>
        </div>
    );
}

export default function DuplicatesPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('companies');   // 'companies' | 'invoices' | 'transactions' | 'history'
    const [repairing, setRepairing] = useState(false);
    const [counts, setCounts] = useState({ companies: 0, invoices: 0, transactions: 0, history: 0 });
    const [summaryRefreshing, setSummaryRefreshing] = useState(false);

    const fetchCounts = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setSummaryRefreshing(true);
        try {
            const [c, i, t, h] = await Promise.all([
                axios.get(`${API}/api/accounting/companies/duplicates`, { headers: headers() }).catch(() => ({ data: { total_groups: 0 } })),
                axios.get(`${API}/api/accounting/invoices/duplicates`, { headers: headers() }).catch(() => ({ data: { groups: [] } })),
                axios.get(`${API}/api/accounting/bank-transactions/duplicates`, { headers: headers() }).catch(() => ({ data: { groups: [] } })),
                axios.get(`${API}/api/accounting/companies/merges?include_undone=false&limit=200`, { headers: headers() }).catch(() => ({ data: { merges: [] } })),
            ]);
            setCounts({
                companies: c.data?.total_groups || 0,
                invoices: (i.data?.groups || []).length || 0,
                transactions: (t.data?.groups || []).length || 0,
                history: (h.data?.merges || []).length || 0,
            });
        } finally { setSummaryRefreshing(false); }
    }, []);
    useEffect(() => { fetchCounts(); }, [fetchCounts]);

    const handleRepairOrphans = async () => {
        if (!window.confirm('Orphan (firma baglantisi eksik) faturalari tara ve dogru firmalara bagla.\n\nGuvenli: var olan faturalar silinmez, sadece contact_id eksik olanlar onarilir.')) return;
        setRepairing(true);
        try {
            const r = await axios.post(`${API}/api/accounting/invoices/repair-orphans`, {}, { headers: headers() });
            const d = r.data;
            if (d.total_orphans === 0) toast.success('Hic orphan fatura yok');
            else toast.success(`${d.total_orphans} orphan fatura onarildi (${d.fixed_by_linked_id} linked + ${d.fixed_by_resolve} resolved, ${d.skipped} atlandi)`);
            fetchCounts();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setRepairing(false); }
    };

    const TABS = [
        { k: 'companies', l: 'Firmalar', count: counts.companies, accent: 'purple' },
        { k: 'invoices', l: 'Faturalar', count: counts.invoices, accent: 'blue' },
        { k: 'transactions', l: 'Transferler', count: counts.transactions, accent: 'red' },
        { k: 'history', l: 'Birlesmisler', count: counts.history, accent: 'emerald', Icon: History },
    ];

    return (
        <DashboardLayout>
            <div className="space-y-4 max-w-[1400px] mx-auto" data-testid="acc-duplicates-page">
                {/* HEADER */}
                <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => navigate('/accounting')}
                        className="h-9 px-3 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] text-sm font-medium rounded-xl flex items-center gap-1.5">
                        <ArrowLeft size={14} /> Muhasebe
                    </button>
                    <h1 className="text-xl font-bold text-[#111827]">Duplicate Kontrol</h1>
                    <button onClick={handleRepairOrphans} disabled={repairing}
                        className="ml-auto h-9 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="acc-dup-repair-orphans"
                        title="contact_id eksik (orphan) faturalari otomatik dogru firmalara bagla">
                        {repairing ? <RefreshCw size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                        {repairing ? 'Onariliyor...' : 'Orphan Faturalari Onar'}
                    </button>
                </div>

                {/* SUMMARY HERO */}
                <DuplicateSummary counts={counts} onRefresh={fetchCounts} refreshing={summaryRefreshing} />

                {/* TAB NAV - minimal underline stili */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                    <div className="px-4 border-b border-[#F3F4F6] flex gap-1 overflow-x-auto">
                        {TABS.map(t => {
                            const Icon = t.Icon;
                            return (
                                <button key={t.k} onClick={() => setTab(t.k)}
                                    className={`px-4 py-3 text-[13px] font-semibold border-b-2 flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? 'border-[#111] text-[#111]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'}`}
                                    data-testid={`acc-dup-tab-${t.k}`}>
                                    {Icon && <Icon size={13} />}
                                    {t.l}
                                    {t.count > 0 && (
                                        <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${tab === t.k ? 'bg-[#111] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                                            {t.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className="p-3">
                        {tab === 'companies' && <DuplicateCompanies onAfterChange={fetchCounts} />}
                        {tab === 'invoices' && <DuplicateInvoices onAfterChange={fetchCounts} />}
                        {tab === 'transactions' && <DuplicateTransactions onAfterChange={fetchCounts} />}
                        {tab === 'history' && <MergeHistory onAfterChange={fetchCounts} />}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

function DuplicateCompanies({ onAfterChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState(null);

    const fetchData = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/accounting/companies/duplicates`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => toast.error('Yuklenemedi'))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { fetchData(); }, [fetchData]);

    const handleMerge = async (sourceId, targetId, sourceName, targetName) => {
        if (!window.confirm(`'${sourceName}' firmasini '${targetName}' firmasiyla birlestir?\nTum kayitlar hedefe tasinir, kaynak silinir.`)) return;
        setMerging(sourceId);
        try {
            const r = await axios.post(`${API}/api/accounting/companies/merge`,
                { source_id: sourceId, target_id: targetId }, { headers: headers() });
            toast.success(`Birlestirildi: ${r.data.txns_repointed} tx + ${r.data.invoices_repointed} fatura tasindi`);
            fetchData();
            onAfterChange?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setMerging(null); }
    };

    if (loading) return <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>;
    if (!data || data.total_groups === 0) {
        return (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center" data-testid="acc-dup-companies-empty">
                <Layers size={32} className="mx-auto mb-2 text-emerald-500" />
                <p className="text-sm font-semibold text-[#111]">Hic duplicate firma yok</p>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Tum kayitlar essiz</p>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="acc-dup-companies">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[12px] text-amber-900">
                    <strong>{data.total_groups} duplicate grup</strong> bulundu (toplam {data.total_duplicates} kayit).
                    Her grupta korumak istediginiz firma satirinin "Hedef" butonuna basin, sonra diger satirlardaki "Bunla Birlestir" butonuyla birlestirin.
                    Tum transferler ve faturalar hedef firmaya tasinir, kaynak silinir.
                </div>
            </div>
            {data.groups.map((g, gi) => <CompanyDupGroup key={gi} group={g} onMerge={handleMerge} merging={merging} />)}
        </div>
    );
}

function CompanyDupGroup({ group, onMerge, merging }) {
    const [targetId, setTargetId] = useState(group.items[0]?.id);
    const typeLabel = group.type === 'iban' ? 'IBAN' : group.type === 'vat' ? 'KDV' : 'Isim';
    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`acc-dup-group-${group.key}`}>
            <div className="px-4 py-2 bg-[#FAFAFA] border-b border-[#F3F4F6] flex items-center gap-2">
                <span className="text-[10px] px-2 py-0.5 rounded font-bold bg-amber-100 text-amber-800">{typeLabel} ESLESMESI</span>
                <span className="text-[11px] text-[#6B7280]">{group.key.split(':').slice(1).join(':')}</span>
                <span className="text-[10px] text-[#9CA3AF] ml-auto">{group.items.length} firma</span>
            </div>
            <div className="divide-y divide-[#F3F4F6]">
                {group.items.map(item => (
                    <div key={item.id} className={`px-4 py-3 flex items-center gap-3 ${targetId === item.id ? 'bg-emerald-50/50' : ''}`}>
                        <input type="radio" name={`target-${group.key}`}
                            checked={targetId === item.id} onChange={() => setTargetId(item.id)}
                            className="w-3.5 h-3.5 accent-emerald-600"
                            data-testid={`acc-dup-target-${item.id}`}
                            title="Bu kaydi koru" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-[#111] truncate">{item.name || '(isimsiz)'}</span>
                                {item.roles?.map(r => <span key={r} className={`text-[9px] px-1 rounded-full ${r === 'customer' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{r === 'customer' ? 'M' : 'T'}</span>)}
                                {item.source && <span className="text-[9px] text-[#9CA3AF]">[{item.source}]</span>}
                            </div>
                            <div className="flex flex-wrap gap-2 text-[10px] text-[#9CA3AF] mt-0.5">
                                {item.vat_number && <span>KDV: {item.vat_number}</span>}
                                {item.iban && <span>IBAN: {item.iban}</span>}
                                <span className="text-[#6B7280] font-semibold">{item.invoice_count} fatura</span>
                                <span className="text-[#6B7280]">{item.tx_count} tx</span>
                            </div>
                        </div>
                        {targetId === item.id ? (
                            <span className="text-[10px] px-2 py-1 bg-emerald-600 text-white rounded-lg font-semibold">Hedef</span>
                        ) : (
                            <button onClick={() => onMerge(item.id, targetId, item.name, group.items.find(i => i.id === targetId)?.name)}
                                disabled={merging === item.id || !targetId}
                                className="h-7 px-2 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[10px] font-semibold rounded-md flex items-center gap-1 disabled:opacity-50"
                                data-testid={`acc-dup-merge-${item.id}`}>
                                {merging === item.id ? <RefreshCw size={10} className="animate-spin" /> : <Layers size={10} />}
                                {merging === item.id ? '...' : 'Bunla Birlestir'}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function DuplicateInvoices({ onAfterChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);  // group/item id
    const [selected, setSelected] = useState(new Set());  // toplu sil icin
    const [bulkBusy, setBulkBusy] = useState(false);

    const fetchData = useCallback(({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        axios.get(`${API}/api/accounting/invoices/duplicates`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => { if (!silent) toast.error('Yuklenemedi'); })
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { fetchData(); }, [fetchData]);

    // Lokal optimistic: silinen item'i gruplardan cikarir, 1 kayit kalan gruplari da siler
    const removeLocally = (ids) => {
        const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
        setData(prev => {
            if (!prev) return prev;
            const newGroups = (prev.groups || [])
                .map(g => ({ ...g, items: g.items.filter(i => !idSet.has(i.id)), count: g.items.filter(i => !idSet.has(i.id)).length }))
                .filter(g => g.items.length >= 2); // 1 veya 0 kayit kalan grup duplicate degil
            return { ...prev, groups: newGroups, total_groups: newGroups.length };
        });
        setSelected(s => { const n = new Set(s); idSet.forEach(x => n.delete(x)); return n; });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu faturayi sil?')) return;
        setBusy(id);
        try {
            await axios.delete(`${API}/api/accounting/invoices/${id}`, { headers: headers() });
            toast.success('Silindi');
            removeLocally(id);
            onAfterChange?.({ silent: true });
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setBusy(null); }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`${ids.length} faturayi sil? Bu islem geri alinamaz.`)) return;
        setBulkBusy(true);
        let ok = 0, fail = 0;
        const okIds = [];
        for (const id of ids) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await axios.delete(`${API}/api/accounting/invoices/${id}`, { headers: headers() });
                ok++; okIds.push(id);
            } catch (e) { fail++; }
        }
        setBulkBusy(false);
        removeLocally(okIds);
        if (fail === 0) toast.success(`${ok} fatura silindi`);
        else toast.warning(`${ok} basarili · ${fail} hata`);
        onAfterChange?.({ silent: true });
    };

    const toggleSel = (id) => setSelected(s => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const toggleGroupSel = (group) => {
        const groupIds = group.items.map(i => i.id);
        const allSel = groupIds.every(id => selected.has(id));
        setSelected(s => {
            const n = new Set(s);
            if (allSel) groupIds.forEach(id => n.delete(id));
            else groupIds.forEach(id => n.add(id));
            return n;
        });
    };
    const selectAllExceptOldest = () => {
        // Her gruptan en eski olani disinda tum item'lari sec — guvenli toplu temizlik onerisi
        const ids = [];
        (data?.groups || []).forEach(g => {
            const sorted = [...g.items].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
            sorted.slice(1).forEach(i => ids.push(i.id));
        });
        setSelected(new Set(ids));
    };

    // BONUS AKSIYON: cross-contact gruplari icin
    // tek tikla "Firmalari birlestir + duplicate faturalari sil"
    const handleAutoFix = async (group) => {
        const items = group.items;
        const contactIds = [...new Set(items.map(i => i.contact_id).filter(Boolean))];
        if (contactIds.length < 2) {
            toast.error('En az 2 farkli firma kaydi gerek');
            return;
        }
        const targetCid = contactIds[0];
        const sourceCids = contactIds.slice(1);
        const targetName = items.find(i => i.contact_id === targetCid)?.contact_name || '?';
        // Duplicate faturalari sil: tum item'lari biri haric (en eski olani koru)
        const sorted = [...items].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        const keepId = sorted[0].id;
        const deleteIds = sorted.slice(1).map(i => i.id);

        const ok = window.confirm(
            `OTOMATIK DUZELT:\n\n` +
            `1) ${sourceCids.length} firma "${targetName}" altinda birlesecek\n` +
            `2) ${deleteIds.length} duplicate fatura silinecek (en eski kayit korunur)\n\n` +
            `Devam edilsin mi? (Birlestirme "Birlesmis Firmalar" tab'inden geri alinabilir)`
        );
        if (!ok) return;

        setBusy(group.items[0].id + '_grp');
        let mergeOk = 0, mergeFail = 0, delOk = 0, delFail = 0;
        const delOkIds = [];
        try {
            // 1) Firmalari birlestir
            for (const srcCid of sourceCids) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await axios.post(`${API}/api/accounting/companies/merge`,
                        { source_id: srcCid, target_id: targetCid }, { headers: headers() });
                    mergeOk++;
                } catch (e) { mergeFail++; console.error('merge fail', srcCid, e); }
            }
            // 2) Fazlalik faturalari sil
            for (const id of deleteIds) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await axios.delete(`${API}/api/accounting/invoices/${id}`, { headers: headers() });
                    delOk++; delOkIds.push(id);
                } catch (e) { delFail++; console.error('delete fail', id, e); }
            }
            const msg = `${mergeOk} firma birlesti · ${delOk} fatura silindi`;
            if (mergeFail || delFail) toast.warning(`${msg} (basarisiz: ${mergeFail}+${delFail})`);
            else toast.success(msg);
            // Optimistic: silinen faturalari kaldir + grup tumden cozulduyse remove
            removeLocally(delOkIds);
            onAfterChange?.({ silent: true });
        } finally { setBusy(null); }
    };

    if (loading) return <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>;
    if (!data || data.total_groups === 0) {
        return (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center" data-testid="acc-dup-invoices-empty">
                <Layers size={32} className="mx-auto mb-2 text-emerald-500" />
                <p className="text-sm font-semibold text-[#111]">Hic duplicate fatura yok</p>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Tum fatura kayitlari essiz</p>
            </div>
        );
    }

    const strictCount = data.strict_count || 0;
    const probableCount = data.probable_count || 0;
    const crossCount = data.cross_contact_count || 0;

    return (
        <div className="space-y-3" data-testid="acc-dup-invoices">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[12px] text-amber-900 leading-relaxed">
                    <strong>{data.total_groups} duplicate fatura grubu</strong> bulundu.
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span>🔴 <strong>{strictCount}</strong> KESIN (ayni firma + no + tarih)</span>
                        <span>🟠 <strong>{probableCount}</strong> OLASILIK (ayni firma + no, farkli tarih)</span>
                        <span>🟡 <strong>{crossCount}</strong> CROSS-CONTACT (farkli firma kaydi)</span>
                    </div>
                </div>
            </div>

            {/* Toolbar: toplu sec / sil */}
            <div className="bg-white border border-[#E5E7EB] rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="acc-dup-inv-toolbar">
                <span className="text-[11px] text-[#6B7280]">
                    {selected.size > 0
                        ? <><strong className="text-[#111]">{selected.size}</strong> fatura secili</>
                        : 'Hizli temizlik:'}
                </span>
                {selected.size === 0 && (
                    <button onClick={selectAllExceptOldest}
                        className="h-7 px-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-[11px] font-semibold rounded-md flex items-center gap-1"
                        data-testid="acc-dup-inv-select-except-oldest">
                        Her gruptan en eskiyi koru, digerlerini sec
                    </button>
                )}
                {selected.size > 0 && (
                    <>
                        <button onClick={() => setSelected(new Set())}
                            className="h-7 px-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-[11px] font-semibold rounded-md"
                            data-testid="acc-dup-inv-clear-sel">
                            Temizle
                        </button>
                        <button onClick={handleBulkDelete} disabled={bulkBusy}
                            className="ml-auto h-7 px-3 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="acc-dup-inv-bulk-delete">
                            {bulkBusy ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                            {bulkBusy ? 'Siliniyor...' : `Sec ile Sil (${selected.size})`}
                        </button>
                    </>
                )}
            </div>

            {data.groups.map((g, gi) => {
                const conf = g.confidence;
                const isCross = conf === 'cross_contact';
                const isProbable = conf === 'probable';
                const grpKey = `${g.key.invoice_number}-${g.key.type}-${gi}`;
                const groupAllSel = g.items.every(i => selected.has(i.id));
                const groupSomeSel = !groupAllSel && g.items.some(i => selected.has(i.id));
                const tone = isCross
                    ? { border: 'border-amber-300', bg: 'bg-amber-50', badge: 'bg-amber-200 text-amber-900', label: '🟡 CROSS-CONTACT' }
                    : isProbable
                    ? { border: 'border-orange-300', bg: 'bg-orange-50', badge: 'bg-orange-200 text-orange-900', label: '🟠 OLASILIK' }
                    : { border: 'border-red-300', bg: 'bg-red-50', badge: 'bg-red-200 text-red-900', label: '🔴 KESIN' };
                return (
                <div key={gi} className={`bg-white rounded-xl border overflow-hidden ${tone.border}`} data-testid={`acc-dup-inv-group-${grpKey}`}>
                    <div className={`px-4 py-2 border-b border-[#F3F4F6] flex items-center gap-2 flex-wrap ${tone.bg}`}>
                        <input type="checkbox"
                            checked={groupAllSel}
                            ref={el => { if (el) el.indeterminate = groupSomeSel; }}
                            onChange={() => toggleGroupSel(g)}
                            className="w-3.5 h-3.5 accent-red-600"
                            title="Gruptaki tum kayitlari sec/cikar"
                            data-testid={`acc-dup-inv-group-sel-${grpKey}`} />
                        <span className="text-sm font-semibold text-[#111]">#{g.key.invoice_number}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${tone.badge}`}>
                            {tone.label}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#E5E7EB] text-[#374151] font-bold">{g.count} kayit</span>
                        <span className="text-[11px] text-[#6B7280] ml-2">{fmtMoney(g.key.total || g.items[0]?.total)} EUR · {g.key.type}</span>
                        {isProbable && (
                            <span className="text-[10px] text-orange-700 italic">⚠ Farkli tarihler — inceleyip dogrula</span>
                        )}
                        {isCross && (
                            <button onClick={() => handleAutoFix(g)}
                                disabled={busy === g.items[0].id + '_grp'}
                                className="ml-auto h-7 px-3 bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
                                data-testid={`acc-dup-inv-autofix-${grpKey}`}
                                title="Firmalari birlestir + duplicate faturalari sil">
                                {busy === g.items[0].id + '_grp' ? <RefreshCw size={10} className="animate-spin" /> : <Layers size={10} />}
                                Otomatik Duzelt
                            </button>
                        )}
                    </div>
                    <div className="divide-y divide-[#F3F4F6]">
                        {g.items.map(item => (
                            <div key={item.id} className={`px-4 py-2 flex items-center gap-3 flex-wrap ${selected.has(item.id) ? 'bg-red-50/40' : ''}`}>
                                <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSel(item.id)}
                                    className="w-3.5 h-3.5 accent-red-600"
                                    data-testid={`acc-dup-inv-sel-${item.id}`} />
                                <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                                    <span className="text-[#6B7280]">{item.invoice_date}</span>
                                    <span className="text-[#111] truncate" title={item.contact_name}>{item.contact_name || '-'}</span>
                                    <span className="font-bold text-[#111]">{fmtMoney(item.total)} EUR</span>
                                    <span className="text-emerald-600">Odenen: {fmtMoney(item.paid_amount)}</span>
                                    <span className="text-[#9CA3AF]">[{item.source || 'manual'}]</span>
                                </div>
                                <button onClick={() => handleDelete(item.id)} disabled={busy === item.id}
                                    className="h-7 px-2 bg-white border border-red-200 text-red-500 hover:bg-red-50 text-[10px] font-semibold rounded-md flex items-center gap-1 disabled:opacity-50"
                                    data-testid={`acc-dup-inv-del-${item.id}`}>
                                    {busy === item.id ? <RefreshCw size={10} className="animate-spin" /> : <Trash2 size={10} />}
                                    Sil
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            );})}
        </div>
    );
}

function MergeHistory({ onAfterChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [includeUndone, setIncludeUndone] = useState(false);
    const [undoing, setUndoing] = useState(null);

    const fetchData = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/accounting/companies/merges?include_undone=${includeUndone}&limit=200`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => toast.error('Yuklenemedi'))
            .finally(() => setLoading(false));
    }, [includeUndone]);
    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUndo = async (mergeId, sourceName, targetName) => {
        if (!window.confirm(`'${sourceName}' firmasini '${targetName}' icinden geri ayir?\n\nKaynak firma yeniden olusturulur, fatura/transferler ona geri baglanir.`)) return;
        setUndoing(mergeId);
        try {
            const r = await axios.post(`${API}/api/accounting/companies/merges/${mergeId}/undo`, {}, { headers: headers() });
            toast.success(`Ayrildi: '${r.data.restored_name}' geri olusturuldu (${r.data.invoices_restored} fatura + ${r.data.txns_restored} tx geri tasindi)`);
            fetchData();
            onAfterChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Geri alma hatasi');
        } finally {
            setUndoing(null);
        }
    };

    if (loading) return <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>;

    const items = data?.items || [];

    return (
        <div className="space-y-3" data-testid="acc-merge-history">
            <div className="flex items-center justify-between">
                <div className="text-xs text-[#6B7280]">
                    Toplam <strong className="text-[#111]">{data?.total || 0}</strong> birlestirme {includeUndone ? '(geri alinmis dahil)' : '(aktif)'}
                </div>
                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" data-testid="acc-merge-toggle-undone">
                    <input type="checkbox" checked={includeUndone} onChange={e => setIncludeUndone(e.target.checked)} className="w-3.5 h-3.5 accent-[#8B5CF6]" />
                    Geri alinmislari da goster
                </label>
            </div>

            {items.length === 0 ? (
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center" data-testid="acc-merge-empty">
                    <History size={32} className="mx-auto mb-2 text-[#9CA3AF]" />
                    <p className="text-sm font-semibold text-[#111]">Henuz birlestirme yok</p>
                    <p className="text-[11px] text-[#9CA3AF] mt-1">Firmalar sekmesinde "Bunla Birlestir" ile bir merge yaptiginizda buraya kaydedilir.</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-[#F9FAFB] text-[10px] text-[#6B7280] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-2">Tarih</th>
                                <th className="text-left px-3 py-2">Kaynak (silinen)</th>
                                <th className="text-left px-3 py-2">Hedef</th>
                                <th className="text-right px-3 py-2">Tasinan</th>
                                <th className="text-right px-3 py-2">Durum</th>
                                <th className="text-right px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                            {items.map(m => {
                                const undone = !!m.undone_at;
                                const sn = m.source_snapshot?.name || '(?)';
                                const tn = m.target_current?.name || m.target_name || '(silinmis)';
                                return (
                                    <tr key={m.id} className={`hover:bg-[#F9FAFB] ${undone ? 'opacity-60' : ''}`} data-testid={`acc-merge-row-${m.id}`}>
                                        <td className="px-3 py-2 text-[#6B7280]">{fmtDate(m.merged_at)}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold text-[#111]">{sn}</div>
                                            {m.source_snapshot?.iban && <div className="text-[10px] text-[#9CA3AF]">{m.source_snapshot.iban}</div>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold text-[#111]">{tn}</div>
                                            {!m.target_exists && <span className="text-[10px] text-red-500">hedef silinmis</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="text-[10px] text-[#6B7280]">
                                                {(m.invoice_ids_moved || []).length} fatura · {(m.tx_ids_moved || []).length} tx
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {undone ? (
                                                <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded bg-[#F3F4F6] text-[#9CA3AF]">Geri alindi</span>
                                            ) : (
                                                <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded bg-emerald-50 text-emerald-700">Aktif</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {!undone && m.target_exists && (
                                                <button onClick={() => handleUndo(m.id, sn, tn)} disabled={undoing === m.id}
                                                    className="h-7 px-2 bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 text-[10px] font-semibold rounded-md flex items-center gap-1 ml-auto disabled:opacity-50"
                                                    data-testid={`acc-merge-undo-${m.id}`}>
                                                    {undoing === m.id ? <RefreshCw size={10} className="animate-spin" /> : <Undo2 size={10} />}
                                                    Ayir
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function DuplicateTransactions({ onAfterChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const fetchData = useCallback(({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        axios.get(`${API}/api/accounting/bank-transactions/duplicates`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => { if (!silent) toast.error('Yuklenemedi'); })
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { fetchData(); }, [fetchData]);

    const removeLocally = (ids) => {
        const idSet = new Set(Array.isArray(ids) ? ids : [ids]);
        setData(prev => {
            if (!prev) return prev;
            const newGroups = (prev.groups || [])
                .map(g => ({ ...g, items: g.items.filter(i => !idSet.has(i.id)), count: g.items.filter(i => !idSet.has(i.id)).length }))
                .filter(g => g.items.length >= 2);
            return { ...prev, groups: newGroups, total_groups: newGroups.length };
        });
        setSelected(s => { const n = new Set(s); idSet.forEach(x => n.delete(x)); return n; });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu banka transferini sil? (Eslesmis ise eslesme de bozulur)')) return;
        setBusy(id);
        try {
            await axios.delete(`${API}/api/accounting/bank-transactions/${id}`, { headers: headers() });
            toast.success('Silindi');
            removeLocally(id);
            onAfterChange?.({ silent: true });
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setBusy(null); }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`${ids.length} transferi sil? Eslesmis olanlar eslesmeyi de bozar. Geri alinamaz.`)) return;
        setBulkBusy(true);
        let ok = 0, fail = 0;
        const okIds = [];
        for (const id of ids) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await axios.delete(`${API}/api/accounting/bank-transactions/${id}`, { headers: headers() });
                ok++; okIds.push(id);
            } catch (e) { fail++; }
        }
        setBulkBusy(false);
        removeLocally(okIds);
        if (fail === 0) toast.success(`${ok} transfer silindi`);
        else toast.warning(`${ok} basarili · ${fail} hata`);
        onAfterChange?.({ silent: true });
    };

    const toggleSel = (id) => setSelected(s => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const toggleGroupSel = (group) => {
        const groupIds = group.items.map(i => i.id);
        const allSel = groupIds.every(id => selected.has(id));
        setSelected(s => {
            const n = new Set(s);
            if (allSel) groupIds.forEach(id => n.delete(id));
            else groupIds.forEach(id => n.add(id));
            return n;
        });
    };
    const selectAllExceptOldest = () => {
        const ids = [];
        (data?.groups || []).forEach(g => {
            const sorted = [...g.items].sort((a, b) => (a.created_at || a.transaction_date || '').localeCompare(b.created_at || b.transaction_date || ''));
            sorted.slice(1).forEach(i => ids.push(i.id));
        });
        setSelected(new Set(ids));
    };

    if (loading) return <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>;
    if (!data || data.total_groups === 0) {
        return (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center" data-testid="acc-dup-tx-empty">
                <Layers size={32} className="mx-auto mb-2 text-emerald-500" />
                <p className="text-sm font-semibold text-[#111]">Hic duplicate transfer yok</p>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Tum banka transferleri essiz</p>
            </div>
        );
    }

    const strictCount = data.strict_count || 0;
    const heurCount = data.heuristic_count || 0;

    return (
        <div className="space-y-3" data-testid="acc-dup-transactions">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[12px] text-amber-900">
                    <strong>{data.total_groups} olasi duplicate transfer grubu</strong> bulundu.
                    <span className="ml-2">🟢 <strong>{strictCount}</strong> kesin (ayni banka referansi)</span>
                    <span className="ml-3">🟡 <strong>{heurCount}</strong> olasilik (ayni gun + ayni tutar + ayni karsi taraf)</span>
                    <div className="text-[11px] mt-1 text-amber-800">
                        Dikkat: Olasilik gruplari gercekten 2 ayri odeme de olabilir (kira, taksit vs). Silmeden once kontrol edin.
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white border border-[#E5E7EB] rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="acc-dup-tx-toolbar">
                <span className="text-[11px] text-[#6B7280]">
                    {selected.size > 0
                        ? <><strong className="text-[#111]">{selected.size}</strong> transfer secili</>
                        : 'Hizli temizlik:'}
                </span>
                {selected.size === 0 && (
                    <button onClick={selectAllExceptOldest}
                        className="h-7 px-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-[11px] font-semibold rounded-md flex items-center gap-1"
                        data-testid="acc-dup-tx-select-except-oldest">
                        Her gruptan en eskiyi koru, digerlerini sec
                    </button>
                )}
                {selected.size > 0 && (
                    <>
                        <button onClick={() => setSelected(new Set())}
                            className="h-7 px-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-[11px] font-semibold rounded-md"
                            data-testid="acc-dup-tx-clear-sel">
                            Temizle
                        </button>
                        <button onClick={handleBulkDelete} disabled={bulkBusy}
                            className="ml-auto h-7 px-3 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="acc-dup-tx-bulk-delete">
                            {bulkBusy ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                            {bulkBusy ? 'Siliniyor...' : `Sec ile Sil (${selected.size})`}
                        </button>
                    </>
                )}
            </div>

            {data.groups.map((g, gi) => {
                const isHeur = g.confidence === 'heuristic';
                const k = g.key || {};
                const sampleAmt = g.items[0]?.amount || 0;
                const isIncoming = sampleAmt >= 0;
                const groupAllSel = g.items.every(i => selected.has(i.id));
                const groupSomeSel = !groupAllSel && g.items.some(i => selected.has(i.id));
                return (
                <div key={gi} className={`bg-white rounded-xl border overflow-hidden ${isHeur ? 'border-amber-300' : 'border-emerald-300'}`} data-testid={`acc-dup-tx-group-${gi}`}>
                    <div className={`px-4 py-2 border-b border-[#F3F4F6] flex items-center gap-2 flex-wrap ${isHeur ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                        <input type="checkbox"
                            checked={groupAllSel}
                            ref={el => { if (el) el.indeterminate = groupSomeSel; }}
                            onChange={() => toggleGroupSel(g)}
                            className="w-3.5 h-3.5 accent-red-600"
                            title="Gruptaki tum kayitlari sec/cikar"
                            data-testid={`acc-dup-tx-group-sel-${gi}`} />
                        {isIncoming ? <ArrowDownLeft size={14} className="text-emerald-600" /> : <ArrowUpRight size={14} className="text-red-600" />}
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${isHeur ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'}`}>
                            {isHeur ? '🟡 OLASILIK' : '🟢 KESIN'}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#E5E7EB] text-[#374151] font-bold">{g.count} kayit</span>
                        {isHeur ? (
                            <span className="text-[11px] text-[#374151]">
                                <strong>{k.date}</strong> · {fmtMoney(k.amount)} EUR · {k.cp}
                            </span>
                        ) : (
                            <span className="text-[11px] text-[#374151]">Bank Ref: <strong>{g.key}</strong></span>
                        )}
                    </div>
                    <div className="divide-y divide-[#F3F4F6]">
                        {g.items.map(item => (
                            <div key={item.id} className={`px-4 py-2 flex items-center gap-3 flex-wrap ${selected.has(item.id) ? 'bg-red-50/40' : ''}`}>
                                <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSel(item.id)}
                                    className="w-3.5 h-3.5 accent-red-600"
                                    data-testid={`acc-dup-tx-sel-${item.id}`} />
                                <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                                    <span className="text-[#6B7280]">{item.transaction_date}</span>
                                    <span className="text-[#111] truncate" title={item.counterparty_name}>{item.counterparty_name || '-'}</span>
                                    <span className={`font-bold ${item.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{item.amount >= 0 ? '+' : ''}{fmtMoney(item.amount)}</span>
                                    <span className="text-[#9CA3AF] truncate" title={item.communication}>{item.communication || '-'}</span>
                                    <span className="text-[#9CA3AF] text-[10px]" title={item.bank_ref}>{item.bank_ref || '-'}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded text-center font-semibold ${item.status === 'matched' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{item.status}</span>
                                </div>
                                <button onClick={() => handleDelete(item.id)} disabled={busy === item.id}
                                    className="h-7 px-2 bg-white border border-red-200 text-red-500 hover:bg-red-50 text-[10px] font-semibold rounded-md flex items-center gap-1 disabled:opacity-50"
                                    data-testid={`acc-dup-tx-del-${item.id}`}>
                                    {busy === item.id ? <RefreshCw size={10} className="animate-spin" /> : <Trash2 size={10} />}
                                    Sil
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            );})}
        </div>
    );
}
